import { Input } from '@react-apps/design-system';
import type {
  GrantPolicyParams,
  GrantPolicySpec,
} from '../../domain/entities/catalog.entity';
import { daysToMs, msToDays } from '../labels';

/**
 * Os campos de `grantPolicyParams` que **a spec do back** manda cobrir — a
 * mesma tabela usada pela concessão (`/admin/perks/grant`) e pela associação a
 * produto. A UI não decide quais campos aparecem: `GET
 * /admin/catalog/perks/grant-policy-kinds` decide. Enviar campo `forbidden`, ou
 * omitir um `required`, é 400 do back desde a fase 2a.
 *
 * O rascunho fica em dias (o operador pensa em dias); a conversão para
 * milissegundos acontece só na saída, em `grantPolicyParamsFromDraft`.
 */
export interface GrantPolicyParamsDraft {
  durationDays: string;
  maxUsages: string;
  renewDays: string;
}

export const EMPTY_GRANT_POLICY_PARAMS_DRAFT: GrantPolicyParamsDraft = {
  durationDays: '',
  maxUsages: '',
  renewDays: '',
};

const isPositive = (value: string) => Number(value) > 0;

/** Todo campo `required` da spec preenchido com número positivo. */
export function isGrantPolicyDraftComplete(
  spec: GrantPolicySpec | undefined,
  draft: GrantPolicyParamsDraft,
): boolean {
  if (!spec) return false;
  const { params } = spec;
  return (
    (params.durationInMilliseconds !== 'required' ||
      isPositive(draft.durationDays)) &&
    (params.maxUsages !== 'required' || isPositive(draft.maxUsages)) &&
    (params.renewEveryInMilliseconds !== 'required' ||
      isPositive(draft.renewDays))
  );
}

/** Só os campos exigidos pela spec — os proibidos nem viajam. */
export function grantPolicyParamsFromDraft(
  spec: GrantPolicySpec,
  draft: GrantPolicyParamsDraft,
): GrantPolicyParams {
  const { params } = spec;
  return {
    ...(params.durationInMilliseconds === 'required'
      ? { durationInMilliseconds: daysToMs(Number(draft.durationDays)) }
      : {}),
    ...(params.maxUsages === 'required'
      ? { maxUsages: Number(draft.maxUsages) }
      : {}),
    ...(params.renewEveryInMilliseconds === 'required'
      ? { renewEveryInMilliseconds: daysToMs(Number(draft.renewDays)) }
      : {}),
  };
}

/**
 * Caminho inverso: valores já resolvidos (um item de preset) → rascunho
 * editável. É como o editor de associação puxa um preset para a coluna da
 * esquerda — a linha entra preenchida e o operador ainda pode mexer.
 *
 * O que o kind proíbe não vira campo, mesmo que o registro de origem carregue o
 * valor. A ida e volta passa por dias, então uma duração que não seja múltiplo
 * exato de um dia é arredondada aqui — o formulário nunca ofereceu granularidade
 * menor que isso.
 */
export function grantPolicyDraftFromValues(
  spec: GrantPolicySpec | undefined,
  values: {
    durationInMilliseconds?: number | null;
    maxUsages?: number | null;
    renewEveryInMilliseconds?: number | null;
  },
): GrantPolicyParamsDraft {
  if (!spec) return EMPTY_GRANT_POLICY_PARAMS_DRAFT;
  const { params } = spec;
  return {
    durationDays:
      params.durationInMilliseconds === 'required' &&
      values.durationInMilliseconds != null
        ? String(msToDays(values.durationInMilliseconds))
        : '',
    maxUsages:
      params.maxUsages === 'required' && values.maxUsages != null
        ? String(values.maxUsages)
        : '',
    renewDays:
      params.renewEveryInMilliseconds === 'required' &&
      values.renewEveryInMilliseconds != null
        ? String(msToDays(values.renewEveryInMilliseconds))
        : '',
  };
}

interface GrantPolicyParamsFieldsProps {
  spec: GrantPolicySpec | undefined;
  draft: GrantPolicyParamsDraft;
  onChange: (draft: GrantPolicyParamsDraft) => void;
  /** Texto exibido quando o kind não pede parâmetro nenhum. */
  emptyMessage?: string;
}

export function GrantPolicyParamsFields({
  spec,
  draft,
  onChange,
  emptyMessage = 'Esta política não pede parâmetros.',
}: GrantPolicyParamsFieldsProps) {
  if (!spec) return null;

  const { params } = spec;
  const asksNothing =
    params.durationInMilliseconds === 'forbidden' &&
    params.maxUsages === 'forbidden' &&
    params.renewEveryInMilliseconds === 'forbidden';

  if (asksNothing) {
    return (
      <p className="text-(--color-text-secondary) text-sm italic">
        {emptyMessage}
      </p>
    );
  }

  const set = (patch: Partial<GrantPolicyParamsDraft>) =>
    onChange({ ...draft, ...patch });

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {params.durationInMilliseconds === 'required' && (
        <Input
          label="Duração (dias)"
          type="number"
          min={1}
          value={draft.durationDays}
          onChange={(e) => set({ durationDays: e.target.value })}
          helperText="Obrigatório para esta política."
        />
      )}
      {params.maxUsages === 'required' && (
        <Input
          label="Máx. de usos"
          type="number"
          min={1}
          value={draft.maxUsages}
          onChange={(e) => set({ maxUsages: e.target.value })}
          helperText="Obrigatório para esta política."
        />
      )}
      {params.renewEveryInMilliseconds === 'required' && (
        <Input
          label="Renova a cada (dias)"
          type="number"
          min={1}
          value={draft.renewDays}
          onChange={(e) => set({ renewDays: e.target.value })}
          helperText="Obrigatório para esta política."
        />
      )}
    </div>
  );
}
